// 9fbef606107a605d69c0edbcd8029e5d
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import upperCase from 'lodash/upperCase';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import {
  parseBoolean,
  configureInternalNavigationFromCMSUrl,
  getModuleModClassSpacing,
} from '@tcp/core/src/utils';
import HorizontalCarousel from '@tcp/core/src/components/common/molecules/HorizontalCarousel';
import { Container, ButtonList, Button } from '../styles/OutfitCarouselPillVariation.style';

const ARROW_VISIBLE_BP = {
  1200: 7,
  1440: 8,
  1920: 9,
  default: 10,
};

const SLIDES_TO_SCROLL_BP = {
  1200: 7,
  1440: 8,
  1920: 9,
  default: 10,
};

const calculateBreakPointByWidth = (innerWidth) => {
  if (innerWidth && innerWidth[0] < 1440) return 1200;
  if (innerWidth && innerWidth[0] < 1920) return 1440;
  if (innerWidth && innerWidth[0] >= 1920) return 1920;
  return 'default';
};

const fetchStringFromAsPathRegex = /^(?:\/c\/)?(.*?)(?:\?|$)/;

const getActivePillVal = (target, action, url, currentPathName) => {
  const matchedStringVal = target === '_modal' && !isEmpty(action) ? action : url;
  const fetchedVal = matchedStringVal.match(fetchStringFromAsPathRegex);
  const fetchCurrentPathName = currentPathName.match(fetchStringFromAsPathRegex);

  return fetchedVal[1] === fetchCurrentPathName[1];
};

const renderHeaderSection = (header, title, subHeader, subTitle) => {
  return (
    <>
      {!isEmpty(header) ? (
        <BodyCopy
          style={title || {}}
          fontFamily="secondary"
          fontSize="fs22"
          fontWeight="bold"
          className="outfit-heading"
          color="gray.2100"
        >
          {header}
        </BodyCopy>
      ) : (
        <></>
      )}
      {!isEmpty(subHeader) ? (
        <BodyCopy
          fontFamily="secondary"
          style={subTitle || {}}
          fontSize="fs22"
          fontWeight="bold"
          className="outfit-subheading"
          color="gray.2100"
        >
          {subHeader}
        </BodyCopy>
      ) : (
        <></>
      )}
    </>
  );
};

const renderButtonList = (
  listData,
  viaModule,
  customWidth,
  customHeight,
  customColor,
  customBGColor,
  customWFontSize,
  pillWidth,
  pillHeight,
  pillColor,
  pillBGColor,
  pillFontSize,
  buttonTileRef,
  currentPathName
  // eslint-disable-next-line max-params
) => {
  const colorVal = !isEmpty(pillColor) ? pillColor?.color : customColor;
  const bgColorVal = !isEmpty(pillBGColor) ? pillBGColor?.bgColor : customBGColor;
  const customWidthValue = !isEmpty(customWidth) ? `${customWidth}%` : '';
  const customHeightValue = !isEmpty(customHeight) ? `${customHeight}%` : '';
  const customWFontSizeValue = !isEmpty(customWFontSize) ? `${customWFontSize}px` : '';
  const widthVal = !isEmpty(pillWidth) ? pillWidth?.width : customWidthValue;
  const heightVal = !isEmpty(pillHeight) ? pillHeight?.height : customHeightValue;
  const fontSizeVal = !isEmpty(pillFontSize) ? pillFontSize?.fontSize : customWFontSizeValue;
  const filterListData = [...listData];

  const activeIndex = filterListData.findIndex(({ link }) => {
    const { target, action, url } = link;
    return getActivePillVal(target, action, url, currentPathName);
  });

  if (activeIndex > -1) {
    const [activeObject] = filterListData.splice(activeIndex, 1);
    filterListData.unshift(activeObject);
  }

  return filterListData.map(({ link }, index) => {
    const { text, target, action, url } = link || {};
    const internalPath = configureInternalNavigationFromCMSUrl(link.url);
    const toPath = `${internalPath}&viaModule=${viaModule}&viaPage=CLP`;
    const activePill = getActivePillVal(target, action, url, currentPathName);

    return (
      <ButtonList key={`${text}_${index}`} ref={buttonTileRef}>
        <Button
          className={`outfit-pill-link ${activePill ? 'active-outfit-pill-button' : ''}`}
          to={toPath}
          asPath={url}
          fontSizeVariation="small"
          dataLocator="outfit-pill-data-locator"
          buttonColor={colorVal}
          backgroundColor={bgColorVal}
          activePill={activePill}
        >
          <BodyCopy
            fontFamily="secondary"
            fontSize={['fs13', 'fs13', 'fs14']}
            fontWeight="black"
            textAlign="center"
            color="gray.800"
            customStyle={{
              width: widthVal,
              height: heightVal,
              color: activePill ? bgColorVal : colorVal,
              fontSize: fontSizeVal,
            }}
          >
            {upperCase(text)}
          </BodyCopy>
        </Button>
      </ButtonList>
    );
  });
};

const OutfitCarouselPillVariation = (props) => {
  const {
    header,
    subHeader,
    linkedListData,
    styleOverrides,
    viaModule,
    customWidth,
    customHeight,
    customColor,
    customBGColor,
    customWFontSize,
    hideHeaderBar,
    windowSize,
    currentPathName,
  } = props;
  const {
    title = '',
    subTitle = '',
    pillWidth = '',
    pillHeight = '',
    pillColor = '',
    pillBGColor = '',
    pillFontSize = '',
    showArrowBeyond = '',
    hideHeaderTitle = '',
  } = styleOverrides;
  const hideHeaderSection =
    !isEmpty(hideHeaderTitle) && parseBoolean(hideHeaderTitle?.isHeaderVisible);
  const isHeaderBarHidden = hideHeaderSection || hideHeaderBar;
  const buttonTileRef = useRef(null);
  const breakPointVal = calculateBreakPointByWidth(windowSize);
  const showArrowNumber = !isEmpty(showArrowBeyond)
    ? parseInt(showArrowBeyond?.arrowVisibleBeyond, 10)
    : ARROW_VISIBLE_BP[breakPointVal];
  const { marginTop = {}, marginBottom = {} } = getModuleModClassSpacing(styleOverrides);

  return (
    <Container
      className="outfit-carousel-pill-design"
      spacingTop={marginTop}
      spacingBottom={marginBottom}
    >
      {!isHeaderBarHidden ? renderHeaderSection(header, title, subHeader, subTitle) : <></>}
      {!isEmpty(linkedListData) ? (
        <HorizontalCarousel
          isArrowVisible={linkedListData.length > showArrowNumber}
          itemRef={buttonTileRef}
          defaultTileWidth={265}
          slidesToScroll={SLIDES_TO_SCROLL_BP[breakPointVal]}
        >
          {renderButtonList(
            linkedListData,
            viaModule,
            customWidth,
            customHeight,
            customColor,
            customBGColor,
            customWFontSize,
            pillWidth,
            pillHeight,
            pillColor,
            pillBGColor,
            pillFontSize,
            buttonTileRef,
            currentPathName
          )}
        </HorizontalCarousel>
      ) : (
        <></>
      )}
    </Container>
  );
};

OutfitCarouselPillVariation.propTypes = {
  header: PropTypes.string,
  subHeader: PropTypes.string,
  styleOverrides: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    pillWidth: PropTypes.string,
    pillHeight: PropTypes.string,
    pillColor: PropTypes.string,
    pillBGColor: PropTypes.string,
    pillFontSize: PropTypes.string,
    showArrowBeyond: PropTypes.string,
    hideHeaderTitle: PropTypes.string,
  }),
  linkedListData: PropTypes.shape(
    PropTypes.arrayOf(
      PropTypes.oneOfType(
        PropTypes.shape({
          link: PropTypes.shape({}),
        })
      )
    )
  ).isRequired,
  viaModule: PropTypes.string,
  customWidth: PropTypes.string,
  customHeight: PropTypes.string,
  customColor: PropTypes.string,
  customBGColor: PropTypes.string,
  customWFontSize: PropTypes.string,
  hideHeaderBar: PropTypes.bool,
  windowSize: PropTypes.shape({}),
  currentPathName: PropTypes.string,
};

OutfitCarouselPillVariation.defaultProps = {
  header: '',
  subHeader: '',
  styleOverrides: {},
  viaModule: '',
  customWidth: '',
  customHeight: '',
  customColor: '',
  customBGColor: '',
  customWFontSize: '',
  hideHeaderBar: false,
  windowSize: {},
  currentPathName: '',
};

OutfitCarouselPillVariation.displayName = 'OutfitCarouselPillVariation';

export default React.memo(OutfitCarouselPillVariation);
export { OutfitCarouselPillVariation as OutfitCarouselPillVariationVanilla };
